import React, { useState, useEffect } from 'react';
import { doc, addDoc, updateDoc, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

const SalesForm = ({ salesData, onClose, onSuccess }) => {
  const [farmers, setFarmers] = useState([]);
  const [flops, setFlops] = useState([]);
  const [formData, setFormData] = useState({
    farmerId: '',
    farmerName: '',
    flopId: '',
    flopName: '',
    date: '',
    delivered: '',
    eggTray: '',
    ogRate: '',
    paymentJama: '',
    paymentReceived: '',
    discount: 0, // Admin can add discount manually
    trayJama: '', // Auto calculated
    rate: '', // Auto calculated (OG Rate - discount)
    totalPrice: '' // Auto calculated (Egg tray * 30 * Rate)
  });
  const [loading, setLoading] = useState(false);
  const [lastTrayJama, setLastTrayJama] = useState(0);
  const [lastPaymentJama, setLastPaymentJama] = useState(0);

  useEffect(() => {
    fetchFarmers();
    fetchFlops();
    if (salesData) {
      setFormData(salesData);
    } else {
      setFormData(prev => ({
        ...prev,
        date: new Date().toISOString().split('T')[0]
      }));
      // fetchLastRecord will be called when farmerId is selected
    }
  }, [salesData]);

  useEffect(() => {
    if (formData.farmerId && formData.flopId) {
      fetchLastRecord(formData.farmerId, formData.flopId);
    } else {
      setLastTrayJama(0);
      setLastPaymentJama(0);
    }
  }, [formData.farmerId, formData.flopId]);

  const fetchFarmers = async () => {
    try {
      const q = query(collection(db, 'farmers'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      setFarmers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching farmers:', error);
    }
  };
  
  const fetchFlops = async () => {
    try {
      const q = query(collection(db, 'flops'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      setFlops(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching flops:', error);
    }
  };

  const fetchLastRecord = async (farmerId, flopId) => {
    try {
      const q = query(
        collection(db, 'sales'),
        orderBy('date', 'desc'),
        limit(1),
        // Only fetch for the selected farmer and flop
        // Firestore does not support orderBy+limit+where in this order, so use a composite index if needed
        // We'll use getDocs and filter in-memory for now for simplicity
      );
      const querySnapshot = await getDocs(q);
      const farmerFlopSales = querySnapshot.docs
        .map(doc => doc.data())
        .filter(sale => sale.farmerId === farmerId && sale.flopId === flopId);
      if (farmerFlopSales.length > 0) {
        const lastRecord = farmerFlopSales[0];
        setLastTrayJama(lastRecord.trayJama || 0);
        setLastPaymentJama(lastRecord.paymentJama || 0);
      } else {
        setLastTrayJama(0);
        setLastPaymentJama(0);
      }
    } catch (error) {
      console.error('Error fetching last record:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'eggTray' || name === 'delivered' || name === 'ogRate' || name === 'discount' || name === 'paymentReceived') {
      calculateFields(name, value);
    }
    if (name === 'farmerId') {
      const selectedFarmer = farmers.find(f => f.id === value);
      setFormData(prev => ({
        ...prev,
        farmerId: value,
        farmerName: selectedFarmer ? selectedFarmer.name : ''
      }));
    }
    if (name === 'flopId') {
      const selectedFlop = flops.find(f => f.id === value);
      setFormData(prev => ({
        ...prev,
        flopId: value,
        flopName: selectedFlop ? selectedFlop.name : ''
      }));
    }
  };

  const calculateFields = (changedField, changedValue) => {
    const currentData = { ...formData };
    if (changedField) {
      currentData[changedField] = changedValue;
    }
    const ogRate = parseFloat(currentData.ogRate) || 0;
    const discount = parseFloat(currentData.discount) || 0;
    const rate = Math.max(0, ogRate - discount);
    currentData.rate = rate.toFixed(2);
    const eggTray = parseFloat(currentData.eggTray) || 0;
    const totalPrice = eggTray * 30 * rate;
    currentData.totalPrice = totalPrice.toFixed(2);
    const delivered = parseFloat(currentData.delivered) || 0;
    const newTrayJama = (parseFloat(lastTrayJama) || 0) + eggTray - delivered;
    currentData.trayJama = newTrayJama.toFixed(2);
    const paymentReceived = parseFloat(currentData.paymentReceived) || 0;
    const newPaymentJama = (parseFloat(lastPaymentJama) || 0) + totalPrice - paymentReceived;
    currentData.paymentJama = newPaymentJama.toFixed(2);
    setFormData(currentData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const salesData = {
        ...formData,
        farmerId: formData.farmerId,
        farmerName: formData.farmerName,
        flopId: formData.flopId,
        flopName: formData.flopName,
        delivered: parseFloat(formData.delivered) || 0,
        eggTray: parseFloat(formData.eggTray) || 0,
        ogRate: parseFloat(formData.ogRate) || 0,
        paymentJama: parseFloat(formData.paymentJama) || 0,
        paymentReceived: parseFloat(formData.paymentReceived) || 0,
        discount: parseFloat(formData.discount) || 0,
        trayJama: parseFloat(formData.trayJama) || 0,
        rate: parseFloat(formData.rate) || 0,
        totalPrice: parseFloat(formData.totalPrice) || 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      if (salesData.id) {
        const docRef = doc(db, 'sales', salesData.id);
        await updateDoc(docRef, salesData);
      } else {
        await addDoc(collection(db, 'sales'), salesData);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving sales data:', error);
      alert('Error saving data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {salesData ? 'Edit Sale Record' : 'Add New Sale Record'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Flop Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Flop *
              </label>
              <select
                name="flopId"
                value={formData.flopId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Flop</option>
                {flops.map((flop) => (
                  <option key={flop.id} value={flop.id}>{flop.name}</option>
                ))}
              </select>
            </div>
            {/* Farmer Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Farmer *
              </label>
              <select
                name="farmerId"
                value={formData.farmerId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Farmer</option>
                {farmers.map((farmer) => (
                  <option key={farmer.id} value={farmer.id}>{farmer.name}</option>
                ))}
              </select>
            </div>

            {/* Manual Entry Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivered *
              </label>
              <input
                type="number"
                name="delivered"
                value={formData.delivered}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Egg Tray *
              </label>
              <input
                type="number"
                name="eggTray"
                value={formData.eggTray}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                OG Rate *
              </label>
              <input
                type="number"
                name="ogRate"
                value={formData.ogRate}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Received *
              </label>
              <input
                type="number"
                name="paymentReceived"
                value={formData.paymentReceived}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount (Admin Only)
              </label>
              <input
                type="number"
                name="discount"
                value={formData.discount}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Discount amount"
              />
            </div>

            {/* Auto Calculated Fields (Read Only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tray Jama (Auto)
              </label>
              <input
                type="number"
                name="trayJama"
                value={formData.trayJama}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Last tray jama + Egg tray - Delivered</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate (Auto)
              </label>
              <input
                type="number"
                name="rate"
                value={formData.rate}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">OG Rate - Discount</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Price (Auto)
              </label>
              <input
                type="number"
                name="totalPrice"
                value={formData.totalPrice}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Egg tray × 30 × Rate</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Jama (Auto)
              </label>
              <input
                type="number"
                name="paymentJama"
                value={formData.paymentJama}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Last payment jama + Total price - Payment received</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (salesData ? 'Update Record' : 'Add Record')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesForm; 